import { inject, Injectable } from '@angular/core';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import { Observable } from 'rxjs';
import { Lot, JobType, LotType, LotPopupInfo } from '@zayan-erp/home-building';

export interface LotChangeWarnings {
  paperworkOnMovedDeals: {
    dealId: string;
    buyerName: string | null;
    newLotNo: string;
    changedAt: string;
    documents: string[];
  }[];
  leftoverDeposits: {
    dealId: string;
    buyerName: string | null;
    count: number;
    total: number;
    depositIds: string[];
    /** The group's deposit ids that have active payments: they stay with the owning deal and cannot be adopted. */
    paidDepositIds: string[];
    /** The amount of those paid deposits. */
    paidTotal: number;
    /** The owning deal is live (it moved away): its buyer's records, read-only on this lot. */
    ownerIsLive: boolean;
    /** The owning deal's current lot. */
    ownerLotNo: string | null;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class LotsService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  readLots(): Observable<Lot[]> {
    return this.#http.getData(`${this.baseUrl}/lot`, { showLoader: true, showMessage: false });
  }

  createLot(Lot: Omit<Lot, 'id'>): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/lot`,
      { showLoader: true, showMessage: true, message: 'Lot created successfully' },
      Lot
    );
  }

  updateLot(Lot: Lot): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/lot/${Lot.id}`, Lot, {
      showLoader: true,
      showMessage: true,
      message: 'Lot updated successfully'
    });
  }

  deleteLot(ids: string[]): Observable<unknown> {
    return this.#http.deleteDataWithBody(`${this.baseUrl}/lot/bulk-delete`, ids, {
      showLoader: true,
      showMessage: true,
      message: 'Lot deleted successfully'
    });
  }

  getLotById(id: string): Observable<Lot | null> {
    return this.#http.getData(`${this.baseUrl}/lot/${id}`, { showLoader: true, showMessage: false });
  }

  getLotPopupInfoById(id: string): Observable<LotPopupInfo | null> {
    return this.#http.getData(`${this.baseUrl}/lot/${id}`, { showLoader: true, showMessage: false });
  }

  getLotChangeWarnings(lotId: string, forDealId?: string): Observable<LotChangeWarnings> {
    const query = forDealId ? `?forDealId=${forDealId}` : '';
    return this.#http.getData<LotChangeWarnings>(`${this.baseUrl}/lot/${lotId}/lot-change-warnings${query}`, {
      showLoader: false,
      showMessage: false
    });
  }

  upsertLot(lot: Omit<Lot, 'id'>[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/lot/bulk-upsert`,
      { showLoader: true, showMessage: true, message: 'Lots added successfully' },
      lot
    );
  }

  // Job Type methods moved from job-type.service.ts
  readJobTypes(): Observable<JobType[]> {
    return this.#http.getData(`${this.baseUrl}/jobtype`, { showLoader: true, showMessage: false });
  }

  // Lot Type methods moved from lot-type.service.ts
  readLotTypes(): Observable<LotType[]> {
    return this.#http.getData(`${this.baseUrl}/lottype`, { showLoader: true, showMessage: false });
  }

  assignModelsToLot(lotId: string, modelIds: string[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/lot/${lotId}/model`,
      { showLoader: true, showMessage: true, message: 'Models assigned successfully' },
      modelIds
    );
  }
}
